import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, CheckCircle2, XCircle, Copy, FileCode2 } from 'lucide-react';
import { apiClient } from '@/api/client';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { ContextMenu } from '@/components/shared/ContextMenu';
import type { ContextMenuItem } from '@/components/shared/ContextMenu';
import { SkeletonTable } from '@/components/shared/Skeleton';
import type { QueryHistoryEntry } from '@/types';

/** The server returns snake_case columns from SQLite; map to our camelCase type. */
interface RawHistoryRow {
  id: string;
  connection_id: string;
  sql: string;
  status: 'success' | 'error';
  duration_ms: number;
  row_count: number | null;
  error: string | null;
  executed_at: string;
}

function mapRow(raw: RawHistoryRow): QueryHistoryEntry {
  return {
    id: raw.id,
    connectionId: raw.connection_id,
    sql: raw.sql,
    status: raw.status,
    durationMs: raw.duration_ms,
    rowCount: raw.row_count,
    error: raw.error,
    executedAt: raw.executed_at,
  };
}

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return ts;
  }
}

function truncateSql(sql: string, maxLen = 80): string {
  const oneLine = sql.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= maxLen) return oneLine;
  return oneLine.slice(0, maxLen) + '...';
}

export function QueryHistoryView() {
  const [searchFilter, setSearchFilter] = useState('');

  const { data: history = [], isLoading } = useQuery<QueryHistoryEntry[]>({
    queryKey: ['queryHistory'],
    queryFn: async () => {
      const raw = await apiClient.get<RawHistoryRow[]>('/api/query/history');
      return raw.map(mapRow);
    },
    refetchInterval: 10_000,
  });

  const filtered = searchFilter
    ? history.filter((h) =>
        h.sql.toLowerCase().includes(searchFilter.toLowerCase())
      )
    : history;

  const handleCopy = useCallback((sql: string) => {
    navigator.clipboard.writeText(sql).catch(() => {
      // Silently fail if clipboard not available
    });
  }, []);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number };
    entry: QueryHistoryEntry;
  } | null>(null);

  const openTab = useWorkspaceStore((s) => s.openTab);

  const handleOpenInEditor = useCallback(
    (entry: QueryHistoryEntry) => {
      const id = `query-from-history-${entry.id}`;
      openTab({
        type: 'query',
        id,
        connectionId: entry.connectionId,
        title: `History ${entry.id.slice(0, 6)}`,
        sql: entry.sql,
      });
    },
    [openTab],
  );

  const handleRowClick = useCallback(
    (entry: QueryHistoryEntry) => {
      handleOpenInEditor(entry);
    },
    [handleOpenInEditor],
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, entry: QueryHistoryEntry) => {
      e.preventDefault();
      setContextMenu({ position: { x: e.clientX, y: e.clientY }, entry });
    },
    [],
  );

  const contextMenuItems: ContextMenuItem[] = contextMenu
    ? [
        {
          label: 'Open in Editor',
          icon: FileCode2,
          onClick: () => handleOpenInEditor(contextMenu.entry),
        },
        {
          label: 'Copy SQL',
          icon: Copy,
          onClick: () => handleCopy(contextMenu.entry.sql),
        },
      ]
    : [];

  return (
    <div className="h-full flex flex-col font-mono">
      {/* Search bar */}
      <div className="px-3 py-1.5 flex-shrink-0">
        <div className="flex items-center gap-1.5 bg-dbgo-input border rounded-md px-2 py-[3px]">
          <Search size={11} className="text-text-muted flex-shrink-0" />
          <input
            type="text"
            placeholder="Filter history..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="bg-transparent text-xs text-text-primary placeholder:text-text-muted outline-none w-full font-mono"
          />
        </div>
      </div>

      {/* Header row */}
      <div className="flex items-center px-3 py-[2px] text-[10px] uppercase text-text-muted tracking-wider flex-shrink-0 border-b border-subtle">
        <span className="w-[130px] flex-shrink-0">Time</span>
        <span className="flex-1 min-w-0">SQL</span>
        <span className="w-[52px] flex-shrink-0 text-center">Status</span>
        <span className="w-[60px] flex-shrink-0 text-right">Duration</span>
        <span className="w-[50px] flex-shrink-0 text-right">Rows</span>
        <span className="w-[28px] flex-shrink-0" />
      </div>

      {/* Entries */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <SkeletonTable rows={5} cols={4} />
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="flex items-center justify-center h-full text-text-muted text-xs">
            {searchFilter ? 'No matching entries' : 'No query history yet'}
          </div>
        )}
        {filtered.map((entry) => (
          <div
            key={entry.id}
            onClick={() => handleRowClick(entry)}
            onContextMenu={(e) => handleContextMenu(e, entry)}
            className="flex items-center px-3 py-[2px] hover:bg-dbgo-tab-hover transition-colors group cursor-pointer"
          >
            {/* Timestamp */}
            <span className="w-[130px] flex-shrink-0 text-[11px] text-text-muted">
              {formatTimestamp(entry.executedAt)}
            </span>

            {/* SQL */}
            <span
              className="flex-1 min-w-0 text-xs text-text-secondary truncate"
              title={entry.sql}
            >
              {truncateSql(entry.sql)}
            </span>

            {/* Status */}
            <span className="w-[52px] flex-shrink-0 flex justify-center">
              {entry.status === 'success' ? (
                <CheckCircle2 size={13} className="text-accent-green" />
              ) : (
                <XCircle size={13} className="text-accent-red" />
              )}
            </span>

            {/* Duration */}
            <span className="w-[60px] flex-shrink-0 text-right text-[11px] text-text-muted">
              {entry.durationMs}ms
            </span>

            {/* Row count */}
            <span className="w-[50px] flex-shrink-0 text-right text-[11px] text-text-muted">
              {entry.rowCount != null ? entry.rowCount : '-'}
            </span>

            {/* Copy button */}
            <span className="w-[28px] flex-shrink-0 flex justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(entry.sql);
                }}
                className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted opacity-0 group-hover:opacity-60 hover:!opacity-100 hover:bg-dbgo-tab-hover transition-opacity"
                title="Copy SQL"
              >
                <Copy size={11} />
              </button>
            </span>
          </div>
        ))}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <ContextMenu
          items={contextMenuItems}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
}
